// Building-block helpers for bounded delta computation between two versions.
//
// meet — fork GLB from fork.common (geometry only).
// revisionBound / bound — walk stop and Delta.getRevisionBound() value; may sit below
// the meet when an observer's referenced object revision requires projection.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { dag, position } from "@hyper-hyper-space/hhs3_dag";
import { json } from "@hyper-hyper-space/hhs3_json";
import { projectForeignBound, resolveRefVersionAtPosition } from "./refs.js";
// Greatest lower bound (meet) of fork.common. Folding over common directly (not an
// antichain) is correct: dominated elements never lower the GLB.
export function computeForkMeet(rawDag, forkCommon) {
    return __awaiter(this, void 0, void 0, function* () {
        return dag.computeMeet([...forkCommon].map((h) => position(h)), (a, b) => rawDag.findForkPosition(a, b).then((f) => f.commonFrontier));
    });
}
// BFS backward from `from`, excluding entries in `bound`. Returns every entry strictly
// above the revision bound — candidates whose state can differ between start and end.
export function walkEntriesBackwardsToBound(rawDag, from, bound) {
    return __awaiter(this, void 0, void 0, function* () {
        const visited = new Set();
        const queue = Array.from(from);
        const walked = [];
        while (queue.length > 0) {
            const hash = queue.shift();
            if (visited.has(hash))
                continue;
            visited.add(hash);
            if (bound.has(hash))
                continue;
            const entry = yield rawDag.loadEntry(hash);
            if (entry === undefined)
                continue;
            walked.push(entry);
            for (const prevHash of json.fromSet(entry.header.prevEntryHashes)) {
                if (!visited.has(prevHash)) {
                    queue.push(prevHash);
                }
            }
        }
        return walked;
    });
}
// Generic, nesting-unaware backward walk. Walks every entry from `end` down to `bound`
// on the raw causal DAG and feeds each into the root accumulator, then returns the
// finalized changes tree. Routing to nested children, unwrapping, and assembling the
// nested subtree all live inside the accumulator, so this helper knows nothing about
// nesting. `start` is plumbed for a future tight change-driven bound (see note below); it
// is unused here.
export function walkDelta(rawDag, start, end, bound, rootAcc) {
    return __awaiter(this, void 0, void 0, function* () {
        const entries = yield walkEntriesBackwardsToBound(rawDag, end, bound);
        for (const entry of entries) {
            yield rootAcc.ingest(entry);
        }
        return rootAcc.finalize();
    });
}
// On the reported revision bound, and a deliberately-deferred tightening.
//
// We report the geometric meet (fork GLB, via computeForkMeet / computeObserverRevisionBound)
// as the revision bound. Its appeal is conceptual: "below the fork GLB the two branches are
// identical, so any resolution agrees" is a single, type-agnostic invariant that needs no
// knowledge of barriers, authorization, or BFT revision. It is provably safe for any object.
//
// A tighter bound is possible: walk in reverse-topological order and track an antichain of
// the deepest entries whose ingest reported an actual change at or below `start` (the
// `changed` boolean ingest already returns is the hook; `start` above is the gate). That
// "change-driven frontier" generally sits above the meet and falls back to `start` when
// nothing changed.
//
// The hard part is composing it across a reference (observer -> observed, e.g. permissioned
// RSet -> RCap). computeObserverRevisionBound projects the observed object's bound into the
// observer DAG; the tight bound is NOT safe to project in general. The reason is BFT
// concurrent-barrier widening: an observer's per-entry authorization check resolves the
// observed object at (at = the entry, from = the view horizon), so for the end view a
// sub-`start` entry E is resolved with from = end. A ref-advance barrier introduced in the
// observer's (start, end) interval that is concurrent to E can flip E's authorization. Such
// a "flipped barrier" can void an entry sitting between the meet and the tight frontier, so
// only the geometric meet is deep enough to be safe to project. Tightening would be safe
// precisely when there is no flipped barrier in the interval (then resolution is monotone
// and the tight bound composes); otherwise the safe floor is the meet over just the flipped
// barriers. We defer all of this: nothing consumes a tighter bound yet, and it would entangle
// type-specific authorization/barrier semantics into what is currently pure DAG geometry.
// Compute the observer's revision bound when a referenced object can revise state
// below the fork meet. `observerMeet` is the fork meet on the observer (input, not the
// result). Assumes referenced.getId() is the ref-advance refId in the observer DAG.
export function computeObserverRevisionBound(observer, observerMeet, observerEnd, referenced) {
    return __awaiter(this, void 0, void 0, function* () {
        const observerDag = yield observer.getScopedDag();
        const refId = referenced.getId();
        const refAtMeet = yield resolveRefVersionAtPosition(observerDag, refId, observerMeet, observerMeet);
        const refAtEnd = yield resolveRefVersionAtPosition(observerDag, refId, observerEnd, observerEnd);
        const referencedRevisionBound = (yield referenced.computeDelta(refAtMeet, refAtEnd)).revisionBound;
        return projectForeignBound(observerDag, refId, yield referenced.getCausalDag(), observerMeet, referencedRevisionBound);
    });
}
// Combine the projected revision bounds of SEVERAL referenced objects into a single
// floor for an observer that watches more than one object (e.g. an Rdb RTableGroup, which
// observes its RSchema plus every bound foreign group). Each `referenced` object can revise
// the observer's verdicts below the fork meet, so we project each one into the observer DAG
// via computeObserverRevisionBound and take their greatest lower bound (along with the meet
// itself). Below the GLB every referenced object is stable AND the two branches' geometry is
// common, so it is a safe walk floor. Each projected bound is already at or below the meet,
// so the GLB is too; an empty `referenced` (or only stable refs) yields the meet unchanged.
export function combineObserverRevisionBounds(observer, observerMeet, observerEnd, referenced) {
    return __awaiter(this, void 0, void 0, function* () {
        const bounds = [observerMeet];
        for (const ref of referenced) {
            bounds.push(yield computeObserverRevisionBound(observer, observerMeet, observerEnd, ref));
        }
        const causalDag = yield observer.getCausalDag();
        return dag.computeMeet(bounds, (a, b) => causalDag.findForkPosition(a, b).then((f) => f.commonFrontier));
    });
}
